import { isAxiosError } from "axios";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { useModalRoute } from "@/hooks/useModalRoute";

import {
  PipelineRunStatusSchema,
  type Document,
  type RenderEmailResponse,
  type PaperType,
} from "@markeroo/shared";
import type { StudentFormData } from "@/features/students/types";
import { buildStudentPayload } from "@/features/students/types";

import {
  useAssignment,
  useAssignmentDocuments,
  useUpdateAssignment,
  useAssessDocument,
  useCreateDocument,
  useUpdateDocument,
  useDeleteDocument,
  useDocumentMarkup,
  useDocumentEditMetrics,
  useCreateShareLink,
  useRevokeShareLink,
  useClass,
  useStudents,
  useUpdateStudent,
  useRenderDocumentEmail,
  useEmailTemplates,
} from "../../api/hooks";
import { useFeatureFlag } from "@/providers/FeatureFlagProvider";
import { SyncButton } from "@/features/google-classroom";
import { PageHeader } from "../../components/PageHeader";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../components/Modal";
import { Select } from "../../components/Select";
import { Tooltip } from "../../components/Tooltip";
import { useNotification } from "../../providers/NotificationProvider";
import type { NotificationKind } from "../../providers/NotificationProvider";
import { useAnalytics } from "../../providers/AnalyticsProvider";
import { useTutorialContext } from "../../providers/TutorialProvider";
import { useSetBreadcrumbs } from "../../hooks/useSetBreadcrumbs";
import { PasteDocumentModal, type PasteDocumentModalSubmitPayload } from "./PasteDocumentModal";
import { DocumentMarkupModal } from "./DocumentMarkupModal";
import { PapersSection } from "./components/PapersSection";
import { PaperViewModal } from "./components/PaperViewModal";
import { ShareModal } from "./components/ShareModal";
import { EditMetricsModal } from "./components/EditMetricsModal";
import { calculateRiskLevel } from "./components/EditMetricsIndicator";
import { CriteriaSummaryCard } from "./components/CriteriaSummaryCard";
import { CriteriaManagerModal } from "./components/CriteriaManagerModal";
import { TutorialInvitationBanner } from "./components/TutorialInvitationBanner";
import { StudentFormModal } from "../classes/components/StudentFormModal";
import { PaperTypeSelector } from "@/components/PaperTypeSelector";
import {
  fieldLabelClass,
  inputClass,
  mutedText,
  smallMutedText,
  pageContainer,
} from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { getPaperTypeLabel } from "@/strings";
import { copyToClipboard, getShareUrl } from "@/lib/clipboard";
import { TutorialRenderer } from "@/lib/tutorials/engine";
import {
  createAssignmentTutorial,
  TUTORIAL_ESSAY_TEXT,
  TUTORIAL_STUDENT_NAME,
  TUTORIAL_EXAMPLE_RULE,
} from "@/lib/tutorials/engine";
import { apiAxios } from "@/api/config";
import { notifications, ui, confirmations } from "@/strings";

//

export function AssignmentDetailPage() {
  const params = useParams<{ assignmentId: string; classId: string }>();
  const assignmentId = params.assignmentId;
  const classId = params.classId;
  const gcIntegrationEnabled = useFeatureFlag("gc_integration");

  if (!assignmentId || !classId) {
    return <Navigate to="/" replace />;
  }

  const { show } = useNotification();
  const { track } = useAnalytics();

  const assignmentQuery = useAssignment(assignmentId);
  const classQuery = useClass(classId);
  const documentsQuery = useAssignmentDocuments(assignmentId, assignmentQuery.data);
  const updateAssignment = useUpdateAssignment();
  const assessDocumentMutation = useAssessDocument();
  const createDocumentMutation = useCreateDocument(assignmentId);
  const updateDocumentMutation = useUpdateDocument(assignmentId);
  const deleteDocumentMutation = useDeleteDocument(assignmentId);

  const createShareMutation = useCreateShareLink(assignmentId);
  const revokeShareMutation = useRevokeShareLink(assignmentId);

  // Hooks for email functionality in ShareModal
  const studentsQuery = useStudents(classId);
  const students = studentsQuery.data?.items ?? [];
  const updateStudentMutation = useUpdateStudent(classId);
  const renderEmailMutation = useRenderDocumentEmail(assignmentId);
  const emailTemplatesQuery = useEmailTemplates();
  const emailTemplates = emailTemplatesQuery.data?.items ?? [];

  // Refs to store tutorial content fetched from backend
  // This ensures the content matches the pre-computed fixtures (single source of truth)
  const tutorialEssayRef = useRef<string>(TUTORIAL_ESSAY_TEXT);
  const tutorialCriteriaPromptRef = useRef<string>(TUTORIAL_EXAMPLE_RULE);

  // Fetch tutorial data from backend on mount (single source of truth)
  useEffect(() => {
    const fetchTutorialData = async () => {
      try {
        const response = await apiAxios.get<{ essay_text: string; criteria_prompt: string }>(
          "/tutorial/data"
        );
        tutorialEssayRef.current = response.data.essay_text;
        tutorialCriteriaPromptRef.current = response.data.criteria_prompt;
      } catch {
        // Fallback to frontend constants if fetch fails
        console.warn("Failed to fetch tutorial data from backend, using fallback");
      }
    };
    void fetchTutorialData();
  }, []);

  const assignment = assignmentQuery.data;
  const classData = classQuery.data;
  const documents = documentsQuery.data;
  const items = documents?.items ?? [];

  // Compute effective template ID for email rendering
  const classDefaultTemplate = emailTemplates.find((t) => t.isDefault) ?? emailTemplates[0];
  const effectiveTemplateId = assignment?.emailTemplateId ?? classDefaultTemplate?.id ?? null;

  // Compute isInProgress based on both assignment status and actual document states
  // This prevents showing "Processing..." when documents have actually failed
  const hasFailedDocuments = items.some((doc) => doc.lastPipelineStatus === "failed");
  const hasProcessingDocuments = items.some(
    (doc) => doc.lastPipelineStatus === "processing" || doc.lastPipelineStatus === "pending"
  );
  const isInProgress =
    (assignment?.status === "queued" || assignment?.status === "processing") &&
    (hasProcessingDocuments || !hasFailedDocuments);

  const TOAST_SUCCESS: NotificationKind = "success";
  const TOAST_ERROR: NotificationKind = "error";

  // Modal route hooks
  const pasteModalAddRoute = useModalRoute({
    pattern: `/classes/:classId/assignments/:assignmentId/papers/add`,
    parentPath: `/classes/:classId/assignments/:assignmentId`,
  });
  const pasteModalEditRoute = useModalRoute({
    pattern: `/classes/:classId/assignments/:assignmentId/papers/:documentId/edit`,
    parentPath: `/classes/:classId/assignments/:assignmentId`,
  });
  const editAssignmentRoute = useModalRoute({
    pattern: `/classes/:classId/assignments/:assignmentId/edit`,
    parentPath: `/classes/:classId/assignments/:assignmentId`,
  });
  const deletePaperRoute = useModalRoute({
    pattern: `/classes/:classId/assignments/:assignmentId/papers/:documentId/delete`,
    parentPath: `/classes/:classId/assignments/:assignmentId`,
  });
  const criteriaManagerRoute = useModalRoute({
    pattern: `/classes/:classId/assignments/:assignmentId/criteria`,
    parentPath: `/classes/:classId/assignments/:assignmentId`,
  });
  const editStudentEmailRoute = useModalRoute({
    pattern: `/classes/:classId/assignments/:assignmentId/students/:studentId/edit-email`,
    parentPath: `/classes/:classId/assignments/:assignmentId`,
  });
  const viewMarkupRoute = useModalRoute({
    pattern: `/classes/:classId/assignments/:assignmentId/papers/:documentId/view`,
    parentPath: `/classes/:classId/assignments/:assignmentId`,
  });
  const viewPaperRoute = useModalRoute({
    pattern: `/classes/:classId/assignments/:assignmentId/papers/:documentId/preview`,
    parentPath: `/classes/:classId/assignments/:assignmentId`,
  });
  const shareModalRoute = useModalRoute({
    pattern: `/classes/:classId/assignments/:assignmentId/papers/:documentId/share`,
    parentPath: `/classes/:classId/assignments/:assignmentId`,
  });

  // Analysis modal uses query param so it can overlay other modals
  const [searchParams, setSearchParams] = useSearchParams();
  const analysisModalDocId = searchParams.get("analysis");
  const openAnalysisModal = useCallback((documentId: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("analysis", documentId);
      return newParams;
    });
  }, [setSearchParams]);
  const closeAnalysisModal = useCallback(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("analysis");
      return newParams;
    });
  }, [setSearchParams]);

  // Derived state from modal routes
  const isPasteModalOpen = pasteModalAddRoute.isOpen || pasteModalEditRoute.isOpen;
  const editingDocumentId = pasteModalEditRoute.params.documentId ?? null;
  const editingDocument = editingDocumentId ? items.find(d => d.id === editingDocumentId) ?? null : null;
  const deleteConfirmDocId = deletePaperRoute.params.documentId ?? null;
  const deleteConfirmDoc = deleteConfirmDocId ? items.find(d => d.id === deleteConfirmDocId) ?? null : null;
  const viewingMarkupDocId = viewMarkupRoute.params.documentId ?? null;
  const viewingPaperDocId = viewPaperRoute.params.documentId ?? null;
  const viewingPaperDoc = viewingPaperDocId ? items.find(d => d.id === viewingPaperDocId) ?? null : null;
  const shareModalDocId = shareModalRoute.params.documentId ?? null;
  const shareModalDoc = shareModalDocId ? items.find(d => d.id === shareModalDocId) ?? null : null;
  const editingStudentForEmailId = editStudentEmailRoute.params.studentId ?? null;

  // Other state (not converted to routes)
  const [preselectedStudentId, setPreselectedStudentId] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [emailTemplateDraft, setEmailTemplateDraft] = useState<string | null>(null);
  // Word count requirements
  const [minWordCountDraft, setMinWordCountDraft] = useState<number | null>(null);
  const [maxWordCountDraft, setMaxWordCountDraft] = useState<number | null>(null);
  const [wordCountValidationError, setWordCountValidationError] = useState<string | null>(null);
  const [shareAction, setShareAction] = useState<{ id: string; type: "create" | "revoke" } | null>(null);
  const [queuingDocId, setQueuingDocId] = useState<string | null>(null);
  const isModalSubmitting = createDocumentMutation.isPending || updateDocumentMutation.isPending;
  const markupQuery = useDocumentMarkup(assignmentId, viewingMarkupDocId);

  // Query for edit metrics when analysis modal is open
  const { data: analysisMetrics } = useDocumentEditMetrics(
    analysisModalDocId ? assignmentId : undefined,
    analysisModalDocId ?? undefined
  );

  // State for email functionality in ShareModal
  const [renderedEmails, setRenderedEmails] = useState<Record<string, RenderEmailResponse>>({});

  // State for paper type selection modal (forced for GC imports without paper type)
  const [paperTypeDraft, setPaperTypeDraft] = useState<PaperType | null>(null);
  const [showPaperTypeError, setShowPaperTypeError] = useState(false);
  const needsPaperType = assignment !== undefined && assignment.paperType === null;

  // Clear queuingDocId once the document's pipeline status confirms it's being processed
  // This ensures the UI doesn't flash back to "Needs Highlighting" before the refetch completes
  useEffect(() => {
    if (!queuingDocId) return;

    const doc = items.find((d) => d.id === queuingDocId);

    // Clear if document shows any valid pipeline status (pending/processing/completed/failed)
    // or if it already has an assessment
    const hasKnownPipelineStatus =
      doc?.lastPipelineStatus != null &&
      PipelineRunStatusSchema.safeParse(doc.lastPipelineStatus).success;

    if (doc && (hasKnownPipelineStatus || doc.hasAssessment)) {
      setQueuingDocId(null);
      return;
    }

    // Timeout fallback: clear after 30 seconds to prevent permanent stuck state
    // This handles edge cases where the backend doesn't create a PipelineRun
    const timeout = setTimeout(() => {
      setQueuingDocId(null);
    }, 30000);

    return () => clearTimeout(timeout);
  }, [queuingDocId, items]);

  // Get tutorial provider context
  const tutorialProviderContext = useTutorialContext();

  const handleOpenCriteriaManager = () => criteriaManagerRoute.open({ classId, assignmentId });
  const handleCloseCriteriaManager = () => criteriaManagerRoute.close();

  const handleCreateShare = (docId: string) => {
    setShareAction({ id: docId, type: "create" });
    createShareMutation.mutate(docId, {
      onSuccess: (document) => {
        // Auto-copy the share link to clipboard
        const student = students.find((s) => s.id === document.studentId);
        if (student?.portalKey) {
          const url = getShareUrl(student.portalKey, docId);
          void copyToClipboard(url);
        }
        show(notifications.share.linkCreated, TOAST_SUCCESS);
        track("share_link_created", { assignmentId, documentId: docId });
        void documentsQuery.refetch();
      },
      onError: () => {
        show(notifications.share.createFailed, TOAST_ERROR);
      },
      onSettled: () => {
        setShareAction(null);
      },
    });
  };

  const handleRevokeShare = (docId: string) => {
    setShareAction({ id: docId, type: "revoke" });
    revokeShareMutation.mutate(docId, {
      onSuccess: () => {
        show(notifications.share.linkRevoked, TOAST_SUCCESS);
        track("share_link_revoked", { assignmentId, documentId: docId });
        void documentsQuery.refetch();
      },
      onError: () => {
        show(notifications.share.revokeFailed, TOAST_ERROR);
      },
      onSettled: () => {
        setShareAction(null);
      },
    });
  };

  const handleCopyShareLink = async (documentId: string, studentId: string) => {
    const student = students.find((s) => s.id === studentId);
    if (!student?.portalKey) {
      show(notifications.share.copyFailed, TOAST_ERROR);
      return;
    }
    const url = getShareUrl(student.portalKey, documentId);
    const success = await copyToClipboard(url);
    if (success) {
      show(notifications.share.linkCopied, TOAST_SUCCESS);
      track("share_link_copied", { assignmentId });
    } else {
      show(notifications.share.copyFailed, TOAST_ERROR);
    }
  };

  // Email functionality for ShareModal
  const getStudentEmail = useCallback(
    (studentId: string) => {
      const student = students.find((s) => s.id === studentId);
      return student?.email;
    },
    [students]
  );

  const handleEditStudentForEmail = (studentId: string) => {
    editStudentEmailRoute.open({ classId, assignmentId, studentId });
  };

  const handleCloseStudentEmailModal = () => {
    if (updateStudentMutation.isPending) {
      return;
    }
    editStudentEmailRoute.close();
  };

  const handleStudentEmailSubmit = (data: StudentFormData) => {
    if (!editingStudentForEmailId) return;

    updateStudentMutation.mutate(
      {
        studentId: editingStudentForEmailId,
        payload: buildStudentPayload(data),
      },
      {
        onSuccess: () => {
          show(notifications.students.updated, TOAST_SUCCESS);
          editStudentEmailRoute.close();
          void studentsQuery.refetch();
        },
        onError: () => {
          show(notifications.students.updateFailed, TOAST_ERROR);
        },
      }
    );
  };

  const getRenderedEmail = useCallback(
    (documentId: string, templateId?: string | null) => {
      // Create cache key that includes template
      const cacheKey = templateId ? `${documentId}:${templateId}` : documentId;
      // Return cached email if available
      if (renderedEmails[cacheKey]) {
        return renderedEmails[cacheKey];
      }
      // Trigger fetch if not already fetching
      if (!renderEmailMutation.isPending) {
        renderEmailMutation.mutate(
          { documentId, templateId: templateId ?? undefined },
          {
            onSuccess: (data) => {
              setRenderedEmails((prev) => ({
                ...prev,
                [cacheKey]: data,
              }));
            },
          }
        );
      }
      return null;
    },
    [renderedEmails, renderEmailMutation]
  );

  const editingStudentData = useMemo(() => {
    if (!editingStudentForEmailId) return null;
    return students.find((s) => s.id === editingStudentForEmailId);
  }, [editingStudentForEmailId, students]);

  const handleOpenPasteModal = () => {
    setPreselectedStudentId(null);
    pasteModalAddRoute.open({ classId, assignmentId });
  };

  const handleUploadForStudent = (studentId: string) => {
    setPreselectedStudentId(studentId);
    pasteModalAddRoute.open({ classId, assignmentId });
  };

  const handleEditDocument = (document: Document) => {
    setPreselectedStudentId(null);
    pasteModalEditRoute.open({ classId, assignmentId, documentId: document.id });
  };

  const handleClosePasteModal = () => {
    if (isModalSubmitting) {
      return;
    }
    setPreselectedStudentId(null);
    if (pasteModalEditRoute.isOpen) {
      pasteModalEditRoute.close();
    } else {
      pasteModalAddRoute.close();
    }
  };

  const handlePasteModalSubmit = async ({ studentId, bodyJson, status, shouldContinue, autoQueue }: PasteDocumentModalSubmitPayload) => {
    try {
      if (editingDocument) {
        const result = await updateDocumentMutation.mutateAsync({
          documentId: editingDocument.id,
          payload: {
            studentId,
            bodyJson,
            status,
            autoQueue,
          },
        });
        if (autoQueue) {
          show(notifications.documents.updatedAndQueued, TOAST_SUCCESS);
        } else {
          show(notifications.documents.updated, TOAST_SUCCESS);
        }
        track("pasted_document_updated", { assignmentId, documentId: result.id, autoQueue });
      } else {
        const result = await createDocumentMutation.mutateAsync({
          studentId,
          source: "text",
          bodyJson,
          status,
          autoQueue,
        });
        if (autoQueue) {
          show(notifications.documents.savedAndQueued, TOAST_SUCCESS);
        } else {
          show(notifications.documents.created, TOAST_SUCCESS);
        }
        track("pasted_document_created", { assignmentId, documentId: result.id, autoQueue });
      }

      // Always clear preselectedStudentId after submission so "Add another" starts fresh
      setPreselectedStudentId(null);

      // Only close modal if not continuing to add another paper
      if (!shouldContinue) {
        if (pasteModalEditRoute.isOpen) {
          pasteModalEditRoute.close();
        } else {
          pasteModalAddRoute.close();
        }
      }

      await Promise.all([
        documentsQuery.refetch(),
        assignmentQuery.refetch(), // Refetch assignment to update isInProgress when autoQueue is true
      ]);
    } catch {
      throw new Error(notifications.documents.createFailed);
    }
  };

  const handleDeleteDocument = (document: Document) => {
    deletePaperRoute.open({ classId, assignmentId, documentId: document.id });
  };

  const handleConfirmDeleteDocument = () => {
    if (!deleteConfirmDoc) return;
    deleteDocumentMutation.mutate(deleteConfirmDoc.id, {
      onSuccess: () => {
        show(notifications.documents.deleted, TOAST_SUCCESS);
        track("document_deleted", { assignmentId, documentId: deleteConfirmDoc.id });
        deletePaperRoute.close();
        void assignmentQuery.refetch();
        void documentsQuery.refetch();
      },
      onError: () => {
        show(notifications.documents.deleteFailed, TOAST_ERROR);
        track("document_delete_failed", { assignmentId, documentId: deleteConfirmDoc.id });
        deletePaperRoute.close();
      },
    });
  };

  const handleCancelDeleteDocument = () => {
    deletePaperRoute.close();
  };

  const handleAssessAllPapers = () => {
    const unassessedPapers = items.filter((doc) => !doc.hasAssessment);
    unassessedPapers.forEach((doc) => {
      assessDocumentMutation.mutate(
        { assignmentId, documentId: doc.id },
        {
          onSuccess: () => {
            show(notifications.documents.queuedBulk(unassessedPapers.length), TOAST_SUCCESS);
            void assignmentQuery.refetch();
            void documentsQuery.refetch();
            track("bulk_assessment_queued", { assignmentId, count: unassessedPapers.length });
          },
          onError: (error) => {
            const message =
              isAxiosError(error) && error.response?.status === 409
                ? notifications.documents.alreadyQueued
                : notifications.documents.queueFailed;
            show(message, TOAST_ERROR);
            track("bulk_assessment_queue_failed", { assignmentId });
          },
        }
      );
    });
  };

  const handleOpenEditTitleModal = () => {
    if (assignment) {
      setTitleDraft(assignment.title);
      setEmailTemplateDraft(assignment.emailTemplateId ?? null);
      // Initialize word count state
      setMinWordCountDraft(assignment.minWordCount ?? null);
      setMaxWordCountDraft(assignment.maxWordCount ?? null);
      editAssignmentRoute.open({ classId, assignmentId });
    }
  };

  const handleCloseEditTitleModal = () => {
    if (updateAssignment.isPending) {
      return;
    }
    setTitleDraft("");
    setEmailTemplateDraft(null);
    // Reset word count state
    setMinWordCountDraft(null);
    setMaxWordCountDraft(null);
    setWordCountValidationError(null);
    editAssignmentRoute.close();
  };

  const handleSaveAssignment = () => {
    if (!assignment) {
      return;
    }

    // For GC-synced assignments, title cannot be changed
    const isGCSynced = !!assignment.gcCourseWorkId;
    const titleChanged = !isGCSynced && titleDraft.trim() !== assignment.title;
    const templateChanged = emailTemplateDraft !== (assignment.emailTemplateId ?? null);

    // Check if word count fields changed
    const minWordCountChanged = minWordCountDraft !== (assignment.minWordCount ?? null);
    const maxWordCountChanged = maxWordCountDraft !== (assignment.maxWordCount ?? null);

    // For non-GC assignments, require a valid title
    if (!isGCSynced && !titleDraft.trim()) {
      return;
    }

    // Validate word count range
    if (minWordCountDraft !== null && maxWordCountDraft !== null && minWordCountDraft > maxWordCountDraft) {
      setWordCountValidationError(notifications.assignments.invalidWordCountRange);
      show(notifications.assignments.invalidWordCountRange, TOAST_ERROR);
      return;
    }
    setWordCountValidationError(null);

    const hasChanges = titleChanged || templateChanged ||
      minWordCountChanged || maxWordCountChanged;

    if (!hasChanges) {
      handleCloseEditTitleModal();
      return;
    }

    updateAssignment.mutate(
      {
        id: assignmentId,
        payload: {
          // Only include title if not GC-synced and it changed
          ...(titleChanged && { title: titleDraft.trim() }),
          emailTemplateId: emailTemplateDraft,
          // Include word count requirements
          minWordCount: minWordCountDraft,
          maxWordCount: maxWordCountDraft,
        },
      },
      {
        onSuccess: () => {
          show(notifications.assignments.updated, TOAST_SUCCESS);
          setTitleDraft("");
          setEmailTemplateDraft(null);
          // Reset word count state
          setMinWordCountDraft(null);
          setMaxWordCountDraft(null);
          editAssignmentRoute.close();
          void assignmentQuery.refetch();
          void documentsQuery.refetch(); // Important for regrade flags!
        },
        onError: () => {
          show(notifications.assignments.updateFailed, TOAST_ERROR);
        },
      }
    );
  };

  const handleSavePaperType = () => {
    if (!assignment || !paperTypeDraft) {
      setShowPaperTypeError(true);
      return;
    }

    updateAssignment.mutate(
      {
        id: assignmentId,
        payload: { paperType: paperTypeDraft },
      },
      {
        onSuccess: () => {
          show(notifications.assignments.updated, TOAST_SUCCESS);
          setPaperTypeDraft(null);
          setShowPaperTypeError(false);
          void assignmentQuery.refetch();
        },
        onError: () => {
          show(notifications.assignments.updateFailed, TOAST_ERROR);
        },
      }
    );
  };

  const documentsLoading = assignmentQuery.isLoading || documentsQuery.isLoading;
  const documentsError = assignmentQuery.isError || documentsQuery.isError;

  const handleAssessDocument = (docId: string) => {
    setQueuingDocId(docId);
    assessDocumentMutation.mutate(
      { assignmentId, documentId: docId },
      {
        onSuccess: () => {
          show(notifications.documents.queued, TOAST_SUCCESS);
          // Trigger refetches - queuingDocId will be cleared by useEffect
          // once the document's lastPipelineStatus updates to pending/processing
          void assignmentQuery.refetch();
          void documentsQuery.refetch();
          track("document_assessment_queued", { documentId: docId, assignmentId });
        },
        onError: (error) => {
          const message =
            isAxiosError(error) && error.response?.status === 409
              ? notifications.documents.alreadyQueued
              : notifications.documents.queueFailed;
          show(message, TOAST_ERROR);
          track("document_assessment_queue_failed", { documentId: docId, assignmentId });
          setQueuingDocId(null);
        },
      }
    );
  };

  const handleRegradeDocument = (docId: string) => {
    setQueuingDocId(docId);
    assessDocumentMutation.mutate(
      { assignmentId, documentId: docId },
      {
        onSuccess: () => {
          show(notifications.documents.requeued, TOAST_SUCCESS);
          // Trigger refetches - queuingDocId will be cleared by useEffect
          // once the document's lastPipelineStatus updates to pending/processing
          void assignmentQuery.refetch();
          void documentsQuery.refetch();
          track("document_regrade_queued", { documentId: docId, assignmentId });
        },
        onError: (error) => {
          const message =
            isAxiosError(error) && error.response?.status === 409
              ? notifications.documents.alreadyQueued
              : notifications.documents.queueFailed;
          show(message, TOAST_ERROR);
          track("document_regrade_queue_failed", { documentId: docId, assignmentId });
          setQueuingDocId(null);
        },
      }
    );
  };


  // Tutorial helper functions (memoized to prevent tutorial recreation)
  const handleFillExampleRule = useCallback(() => {
    // Open the criteria manager modal
    criteriaManagerRoute.open({ classId, assignmentId });

    // Wait for the modal to render, then fill the textarea
    setTimeout(() => {
      const textarea = document.querySelector<HTMLTextAreaElement>(
        '[placeholder*="working with my students"]'
      );
      if (textarea) {
        // Set the value using native input setter
        const descriptor = Object.getOwnPropertyDescriptor.call(
          Object,
          window.HTMLTextAreaElement.prototype,
          'value'
        );
        const nativeInputValueSetter = descriptor?.set?.bind(textarea);
        if (nativeInputValueSetter) {
          nativeInputValueSetter(tutorialCriteriaPromptRef.current);
        }

        // Trigger React's onChange event
        const event = new Event('input', { bubbles: true });
        textarea.dispatchEvent(event);
      }
    }, 100);
  }, [criteriaManagerRoute, classId, assignmentId]);

  const handleFillExamplePaper = useCallback(() => {
    // Open the paste modal
    pasteModalAddRoute.open({ classId, assignmentId });

    // Wait for the modal to render and editor to be ready
    setTimeout(() => {
      // Fill the student dropdown - need to create student first or select existing
      const studentSelect = document.querySelector<HTMLSelectElement>('select');
      if (studentSelect) {
        // Check if "Tutorial Student" already exists
        const tutorialStudentOption = Array.from(studentSelect.options).find(
          (opt) => opt.text === TUTORIAL_STUDENT_NAME
        );

        if (tutorialStudentOption) {
          // Select existing tutorial student
          studentSelect.value = tutorialStudentOption.value;
          const event = new Event('change', { bubbles: true });
          studentSelect.dispatchEvent(event);
        } else {
          // Open create student modal
          studentSelect.value = '__create_new__';
          const event = new Event('change', { bubbles: true });
          studentSelect.dispatchEvent(event);

          // Wait for student modal to open, then fill it
          setTimeout(() => {
            const nameInput = document.querySelector<HTMLInputElement>('input[type="text"]');
            if (nameInput) {
              const descriptor = Object.getOwnPropertyDescriptor.call(
                Object,
                window.HTMLInputElement.prototype,
                'value'
              );
              const nativeInputValueSetter = descriptor?.set?.bind(nameInput);
              if (nativeInputValueSetter) {
                nativeInputValueSetter(TUTORIAL_STUDENT_NAME);
              }
              const event = new Event('input', { bubbles: true });
              nameInput.dispatchEvent(event);
            }
          }, 100);
        }
      }

      // Fill the editor with essay text from backend (single source of truth)
      const editorDiv = document.querySelector<HTMLDivElement>('.tiptap');
      if (editorDiv) {
        // Paste the text into the editor
        editorDiv.focus();
        document.execCommand('insertText', false, tutorialEssayRef.current);
      }
    }, 100);
  }, [pasteModalAddRoute, classId, assignmentId]);

  // Create the tutorial with helper callbacks (memoized to prevent infinite loop)
  const assignmentTutorial = useMemo(() =>
    createAssignmentTutorial({
      onFillExampleRule: handleFillExampleRule,
      onFillExamplePaper: handleFillExamplePaper,
      tutorialContent: {
        essayText: tutorialEssayRef.current,
        criteriaPrompt: tutorialCriteriaPromptRef.current,
      },
    }),
    [handleFillExampleRule, handleFillExamplePaper]
  );

  // Memoize tutorial context to prevent unnecessary re-renders
  const tutorialContext = useMemo(() => ({
    assignment,
    documents: items,
  }), [assignment, items]);

  // Set breadcrumbs for this page
  useSetBreadcrumbs([
    {
      label: classData?.name ?? "Class",
      to: `/classes/${classId}`,
    },
    {
      label: assignment?.title ?? "Assignment",
      to: `/classes/${classId}/assignments/${assignmentId}`,
    },
  ]);

  return (
    <TutorialRenderer
      tutorial={assignmentTutorial}
      context={tutorialContext}
    >
      <div className={`${pageContainer} gap-8`}>
        <PageHeader>
          <div className="flex flex-col gap-2">
            <PageHeader.Title>
              {assignment?.title ?? ui.labels.assignment}
            </PageHeader.Title>
            {assignment && (
              <div className={smallMutedText}>
                {getPaperTypeLabel(assignment.paperType, classData?.defaultElaGradeLevel)}
              </div>
            )}
          </div>
          <PageHeader.Actions>
            {gcIntegrationEnabled && assignment?.gcCourseWorkId && (
              <SyncButton classId={classId} />
            )}
            <button
              type="button"
              onClick={handleOpenEditTitleModal}
              className={buttonVariants.secondary}
            >
              {ui.actions.edit} {ui.labels.assignment}
            </button>
          </PageHeader.Actions>
        </PageHeader>

        <TutorialInvitationBanner
          tutorialId="assignment-first-steps"
          totalSteps={assignmentTutorial.steps.length}
          shouldShow={
            items.length === 0 &&
            !tutorialProviderContext.activeTutorialId &&
            tutorialProviderContext.shouldShowTutorial("assignment-first-steps")
          }
        />

        <CriteriaSummaryCard
          data-tutorial-target="criteria-summary"
          criteria={assignment?.criteria ?? []}
          onOpenManager={handleOpenCriteriaManager}
        />

        <PapersSection
          assignmentId={assignmentId}
          items={items}
          isInProgress={isInProgress}
          overLimit={false}
          documentsLoading={documentsLoading}
          documentsError={documentsError}
          assessDocumentMutation={assessDocumentMutation}
          deleteDocumentMutation={deleteDocumentMutation}
          deleteConfirmDoc={deleteConfirmDoc}
          queuingDocId={queuingDocId}
          onOpenPasteModal={handleOpenPasteModal}
          onAssessAllPapers={handleAssessAllPapers}
          onEditDocument={handleEditDocument}
          onDeleteDocument={handleDeleteDocument}
          onViewMarkup={(docId) => viewMarkupRoute.open({ classId, assignmentId, documentId: docId })}
          onViewPaper={(docId) => viewPaperRoute.open({ classId, assignmentId, documentId: docId })}
          onAssessDocument={handleAssessDocument}
          onRegradeDocument={handleRegradeDocument}
          onOpenShareModal={(docId) => shareModalRoute.open({ classId, assignmentId, documentId: docId })}
          isGCSyncedAssignment={!!assignment?.gcCourseWorkId}
          students={students}
          onUploadForStudent={handleUploadForStudent}
        />
      </div>
      <PasteDocumentModal
        isOpen={isPasteModalOpen}
        assignmentName={assignment?.title ?? ui.labels.assignment}
        classId={assignment?.classId ?? ""}
        defaultDocumentStatus={assignment?.defaultDocumentStatus ?? "draft"}
        initialDocument={editingDocument}
        isSubmitting={isModalSubmitting}
        onClose={handleClosePasteModal}
        onSubmit={handlePasteModalSubmit}
        preselectedStudentId={preselectedStudentId}
      />
      <DocumentMarkupModal
        isOpen={viewMarkupRoute.isOpen}
        document={markupQuery.data ?? null}
        studentName={items.find(doc => doc.id === viewingMarkupDocId)?.studentName ?? null}
        isLoading={markupQuery.isLoading}
        isError={markupQuery.isError}
        onRetry={() => void markupQuery.refetch()}
        onClose={() => viewMarkupRoute.close()}
        documentId={viewingMarkupDocId || undefined}
        assignmentId={assignmentId}
        classId={classId}
        documentStatus={items.find(doc => doc.id === viewingMarkupDocId)?.status}
        shareToken={items.find(doc => doc.id === viewingMarkupDocId)?.shareToken ?? null}
        portalKey={(() => {
          const doc = items.find(d => d.id === viewingMarkupDocId);
          const student = doc ? students.find(s => s.id === doc.studentId) : null;
          return student?.portalKey ?? null;
        })()}
        onDocumentUpdate={() => {
          void markupQuery.refetch();
          void documentsQuery.refetch();
        }}
        studentEmail={(() => {
          const doc = items.find(d => d.id === viewingMarkupDocId);
          return doc ? getStudentEmail(doc.studentId) : null;
        })()}
        renderedEmail={viewingMarkupDocId ? getRenderedEmail(viewingMarkupDocId, effectiveTemplateId) : null}
        isLoadingEmail={renderEmailMutation.isPending}
        onCreateShare={handleCreateShare}
        isCreatingShare={shareAction?.type === "create"}
        onOpenAnalysis={viewingMarkupDocId ? () => openAnalysisModal(viewingMarkupDocId) : undefined}
      />

      <PaperViewModal
        isOpen={viewPaperRoute.isOpen}
        onClose={() => viewPaperRoute.close()}
        document={viewingPaperDoc}
        studentName={viewingPaperDoc?.studentName ?? ""}
        classId={classId}
        assignmentId={assignmentId}
        onOpenAnalysis={viewingPaperDocId ? () => openAnalysisModal(viewingPaperDocId) : undefined}
      />

      <ShareModal
        isOpen={shareModalRoute.isOpen}
        onClose={() => shareModalRoute.close()}
        document={shareModalDoc}
        studentName={shareModalDoc?.studentName ?? ""}
        studentEmail={shareModalDoc ? getStudentEmail(shareModalDoc.studentId) : null}
        papers={(() => {
          if (!shareModalDoc) return [];
          const studentPapers = items.filter((d) => d.studentId === shareModalDoc.studentId);
          return studentPapers.filter(
            (d) => new Date(d.createdAt) <= new Date(shareModalDoc.createdAt),
          );
        })()}
        isCreating={shareAction?.id === shareModalDocId && shareAction.type === "create"}
        isRevoking={shareAction?.id === shareModalDocId && shareAction.type === "revoke"}
        onCreateShare={handleCreateShare}
        onRevokeShare={handleRevokeShare}
        onCopyShareLink={handleCopyShareLink}
        onEditStudent={shareModalDoc ? () => handleEditStudentForEmail(shareModalDoc.studentId) : undefined}
        renderedEmail={shareModalDocId ? getRenderedEmail(shareModalDocId, effectiveTemplateId) : null}
        isLoadingEmail={renderEmailMutation.isPending}
      />

      {analysisMetrics && analysisModalDocId && (
        <EditMetricsModal
          isOpen={!!analysisModalDocId}
          onClose={closeAnalysisModal}
          metrics={analysisMetrics}
          riskLevel={calculateRiskLevel(analysisMetrics)}
          replayContext={{ classId, assignmentId, documentId: analysisModalDocId }}
        />
      )}

      <Modal isOpen={deletePaperRoute.isOpen} onClose={handleCancelDeleteDocument} size="sm" aria-label={`${ui.actions.confirm} ${ui.actions.delete}`}>
        <ModalHeader title={`${ui.actions.delete} ${ui.labels.paper}`} onClose={handleCancelDeleteDocument} />
        <ModalBody>
          <p className={mutedText}>{confirmations.documents.delete}</p>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className={buttonVariants.secondary}
            onClick={handleCancelDeleteDocument}
          >
            {ui.actions.cancel}
          </button>
          <button
            className={buttonVariants.danger}
            type="button"
            onClick={handleConfirmDeleteDocument}
            disabled={deleteDocumentMutation.isPending}
          >
            {deleteDocumentMutation.isPending ? ui.actions.deleting : ui.actions.delete}
          </button>
        </ModalFooter>
      </Modal>
      
      <Modal isOpen={editAssignmentRoute.isOpen} onClose={handleCloseEditTitleModal} size="md">
        <ModalHeader title={`${ui.actions.edit} ${ui.labels.assignment}`} onClose={handleCloseEditTitleModal} />
        <ModalBody>
          <div className="flex flex-col gap-4">
            {/* Title field - read-only for GC-synced assignments */}
            <label htmlFor="assignment-title" className={fieldLabelClass}>
              {ui.labels.title}
              {assignment?.gcCourseWorkId ? (
                <Tooltip content={ui.googleClassroom.managedByGoogleClassroom} fullWidth>
                  <input
                    id="assignment-title"
                    type="text"
                    className={`${inputClass} disabled:bg-surface-secondary disabled:text-theme-muted disabled:cursor-not-allowed`}
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    disabled
                  />
                </Tooltip>
              ) : (
                <input
                  id="assignment-title"
                  type="text"
                  className={inputClass}
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  autoFocus
                />
              )}
            </label>
            {emailTemplates.length > 0 && (
              <label htmlFor="email-template" className={fieldLabelClass}>
                {ui.labels.emailTemplate}
                <Select
                  id="email-template"
                  value={emailTemplateDraft ?? ""}
                  onChange={(e) => setEmailTemplateDraft(e.target.value || null)}
                >
                  <option value="">{ui.labels.useDefault}</option>
                  {emailTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Select>
              </label>
            )}

            {/* Word Count Requirements */}
            <div className="mt-4 space-y-4">
              <h4 className="text-sm font-medium text-theme-secondary">{ui.labels.wordCountRequirements}</h4>
              {/* Word count settings */}
              <div className="grid grid-cols-2 gap-4">
                <label htmlFor="min-word-count" className={fieldLabelClass}>
                  {ui.labels.minWordCount} <span className="text-theme-muted">{ui.labels.wordCountOptional}</span>
                  <input
                    id="min-word-count"
                    type="number"
                    min="0"
                    max="50000"
                    step="1"
                    className={`${inputClass}${wordCountValidationError ? " border-semantic-error focus:border-semantic-error focus:ring-semantic-error" : ""}`}
                    value={minWordCountDraft ?? ""}
                    onChange={(e) => {
                      setMinWordCountDraft(e.target.value ? parseInt(e.target.value, 10) : null);
                      setWordCountValidationError(null);
                    }}
                    aria-invalid={!!wordCountValidationError}
                    aria-describedby={wordCountValidationError ? "word-count-error" : undefined}
                  />
                </label>
                <label htmlFor="max-word-count" className={fieldLabelClass}>
                  {ui.labels.maxWordCount} <span className="text-theme-muted">{ui.labels.wordCountOptional}</span>
                  <input
                    id="max-word-count"
                    type="number"
                    min="0"
                    max="50000"
                    step="1"
                    className={`${inputClass}${wordCountValidationError ? " border-semantic-error focus:border-semantic-error focus:ring-semantic-error" : ""}`}
                    value={maxWordCountDraft ?? ""}
                    onChange={(e) => {
                      setMaxWordCountDraft(e.target.value ? parseInt(e.target.value, 10) : null);
                      setWordCountValidationError(null);
                    }}
                    aria-invalid={!!wordCountValidationError}
                    aria-describedby={wordCountValidationError ? "word-count-error" : undefined}
                  />
                </label>
              </div>
              {wordCountValidationError && (
                <p id="word-count-error" className="text-sm text-semantic-error" role="alert">
                  {wordCountValidationError}
                </p>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className={buttonVariants.secondary}
            onClick={handleCloseEditTitleModal}
            disabled={updateAssignment.isPending}
          >
            {ui.actions.cancel}
          </button>
          <button
            type="button"
            className={buttonVariants.primary}
            onClick={handleSaveAssignment}
            disabled={updateAssignment.isPending || (!assignment?.gcCourseWorkId && !titleDraft.trim())}
          >
            {updateAssignment.isPending ? ui.actions.saving : ui.actions.save}
          </button>
        </ModalFooter>
      </Modal>

      {/* Paper Type Selection Modal - forced for GC imports without paper type */}
      <Modal
        isOpen={needsPaperType}
        onClose={() => {
          // No-op: This modal cannot be closed without selecting a paper type
        }}
        size="md"
        aria-label={ui.labels.paperTypeSelectorTitle}
        role="alertdialog"
      >
        <ModalHeader title={ui.labels.paperType} />
        <ModalBody>
          <PaperTypeSelector
            value={paperTypeDraft}
            onChange={(value) => {
              setPaperTypeDraft(value);
              setShowPaperTypeError(false);
            }}
            showError={showPaperTypeError}
          />
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className={buttonVariants.primary}
            onClick={handleSavePaperType}
            disabled={updateAssignment.isPending || !paperTypeDraft}
          >
            {updateAssignment.isPending ? ui.actions.saving : ui.actions.continue}
          </button>
        </ModalFooter>
      </Modal>

      <CriteriaManagerModal
        isOpen={criteriaManagerRoute.isOpen}
        onClose={handleCloseCriteriaManager}
        assignmentId={assignmentId}
        assignmentName={assignment?.title}
        criteria={assignment?.criteria ?? []}
        gradeLevel={classData?.defaultElaGradeLevel ?? null}
        paperType={assignment?.paperType ?? null}
        criteriaAreCustom={assignment?.criteriaAreCustom ?? false}
        matchingTemplate={assignment?.matchingTemplate ?? null}
      />
      {editingStudentData && (
        <StudentFormModal
          isOpen={editStudentEmailRoute.isOpen}
          onClose={handleCloseStudentEmailModal}
          onSubmit={handleStudentEmailSubmit}
          isSubmitting={updateStudentMutation.isPending}
          initialStudent={editingStudentData}
          defaultElaGradeLevel={classData?.defaultElaGradeLevel ?? undefined}
        />
      )}
    </TutorialRenderer>
  );
}
